import { useRef, useState } from "react";
import { Check, Focus, RotateCw, Star } from "lucide-react";
import { Button } from "@dashai/components/ui/button";
import { Command, CommandInput, CommandItem, CommandList } from "@dashai/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@dashai/components/ui/dialog";
import { cn, formatClientName } from "@dashai/lib/utils";
import { foldSearch } from "@/utils/searchFold";

interface WorkspacePickerProps {
  open: boolean;
  allowedClients: string[];
  currentClient: string | null;
  // The workspace the user lands in at sign-in; its row carries the filled star.
  defaultWorkspace: string | null;
  isStaff: boolean;
  focusMode: boolean;
  workspaceListLoaded: boolean;
  workspaceListError: boolean;
  returnFocusRef: React.RefObject<HTMLButtonElement | null>;
  onRetryWorkspaceList?: () => void;
  onSetDefaultWorkspace?: (client: string) => void;
  // True while a default-workspace save is in flight; every star is disabled.
  savingDefaultWorkspace?: boolean;
  onClientChange: (client: string) => void;
  onClose: () => void;
}

export function WorkspacePicker({
  open,
  allowedClients,
  currentClient,
  defaultWorkspace,
  isStaff,
  focusMode,
  workspaceListLoaded,
  workspaceListError,
  returnFocusRef,
  onRetryWorkspaceList,
  onSetDefaultWorkspace,
  savingDefaultWorkspace = false,
  onClientChange,
  onClose,
}: WorkspacePickerProps) {
  const [query, setQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const presenting = isStaff && focusMode;
  const ready = workspaceListLoaded && !workspaceListError;
  const foldedQuery = foldSearch(query);
  const visibleClients = ready
    ? allowedClients
        .filter((client) => !presenting || client === currentClient)
        .filter((client) => foldSearch(formatClientName(client)).includes(foldedQuery))
        .slice()
        .sort((a, b) => formatClientName(a).localeCompare(formatClientName(b)))
    : [];
  const selection = visibleClients.includes(selectedClient)
    ? selectedClient
    : (visibleClients[0] ?? "");

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="w-[calc(100%-2rem)] max-w-md gap-0 overflow-hidden rounded-xl bg-popover p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          setQuery("");
          setSelectedClient("");
          inputRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusRef.current?.focus();
        }}
      >
        <div className="px-5 pb-4 pt-5 pr-12">
          <DialogTitle>Switch workspace</DialogTitle>
          <DialogDescription className="mt-1">Choose where you want to work.</DialogDescription>
        </div>
        {presenting && (
          <p className="mx-3 mb-3 flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm">
            <Focus aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            Turn off Presenting to switch workspace
          </p>
        )}
        {workspaceListError && (
          <div className="mx-3 mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted px-3 py-2">
            <p role="alert" className="text-sm text-destructive">
              Couldn't load your workspaces
            </p>
            <Button variant="outline" size="sm" onClick={onRetryWorkspaceList}>
              <RotateCw aria-hidden="true" className="mr-2 h-3.5 w-3.5" />
              Retry
            </Button>
          </div>
        )}
        <Command
          label="Search workspaces"
          shouldFilter={false}
          value={selection}
          onValueChange={setSelectedClient}
          loop
        >
          <CommandInput
            ref={inputRef}
            aria-label="Search workspaces"
            placeholder="Search workspaces…"
            value={query}
            onValueChange={(value) => {
              setQuery(value);
              setSelectedClient("");
            }}
          />
          <CommandList className="max-h-[min(300px,45dvh)] p-1">
            {!ready && !workspaceListError && (
              <p role="status" className="px-3 py-6 text-center text-sm text-muted-foreground">
                Loading workspaces…
              </p>
            )}
            {ready && visibleClients.length === 0 && (query.trim() || !presenting) && (
              <p
                role="status"
                className="break-words px-3 py-6 text-center text-sm text-muted-foreground"
              >
                {query.trim() ? <>No workspace matches “{query}”</> : "No workspaces available"}
              </p>
            )}
            {visibleClients.map((client) => {
              const isDefault = client === defaultWorkspace;
              const name = formatClientName(client);
              return (
                <CommandItem
                  key={client}
                  value={client}
                  onSelect={() => {
                    onClientChange(client);
                    onClose();
                  }}
                  className="group gap-2 py-2.5 data-[selected=true]:bg-muted data-[selected=true]:text-foreground"
                >
                  <Check
                    aria-hidden="true"
                    className={cn("h-4 w-4 shrink-0", client !== currentClient && "opacity-0")}
                  />
                  <span className="min-w-0 flex-1 truncate">{name}</span>
                  {client === currentClient && (
                    <span className="text-xs text-muted-foreground group-data-[selected=true]:text-foreground">
                      Current
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label={
                      isDefault
                        ? `${name} is your default workspace`
                        : `Set ${name} as default workspace`
                    }
                    aria-pressed={isDefault}
                    disabled={savingDefaultWorkspace}
                    className="shrink-0 rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    onKeyDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!isDefault) onSetDefaultWorkspace?.(client);
                    }}
                  >
                    <Star
                      aria-hidden="true"
                      className={cn(
                        "h-3.5 w-3.5 group-data-[selected=true]:text-foreground",
                        isDefault ? "fill-current text-primary" : "text-muted-foreground"
                      )}
                    />
                  </button>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
